import {createStyles, Flex, MantineNumberSize, PasswordInput} from '@mantine/core';
import React, {useState} from 'react';

import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

/**
 * Creates a function component suitable for passing to PasswordInput.visibilityToggleIcon but that
 * incorporates the name of the input in the tooltip message.
 */
function makeVisibilityToggleIconComponent(
  name: string
): (props: {reveal: boolean; size: MantineNumberSize}) => JSX.Element {
  return ({reveal}: {reveal: boolean; size: MantineNumberSize}): JSX.Element => (
    <Tooltip
      text={reveal ? `Hide ${name}` : `Show ${name}`}
      placement={FloatingPlacement.TOP}
      disableContentSelection={true}
    >
      <BenchlingIcon
        altText={reveal ? `Hide ${name}` : `Show ${name}`}
        // Don't actually make a Button, because Mantine puts this inside a button and you can't nest buttons
        // But do use the circle icon button styles
        className="button--circleIcon"
        icon={reveal ? 'eye-slash' : 'eye'}
        style={{width: '12px', height: '12px'}}
      />
    </Tooltip>
  );
}

const useStyles = createStyles((theme) => ({
  description: {
    color: theme.colors.gray[7],
    fontSize: theme.fontSizes.sm,
  },
  error: {
    display: 'none',
  },
  input: {
    '&&': {
      height: '28px',
      border: `1px solid ${theme.colors.gray[4]} !important`,
      borderRadius: `${theme.radius.md} !important`,
    },
  },
  innerInput: {
    height: '28px',
  },
  visibilityToggle: {
    borderRadius: '14px',
    height: '28px',
    minWidth: '28px',
    width: '28px',
    marginRight: theme.spacing.xl,
  },
  immutableValueWrapper: {
    flexGrow: 1,
  },
}));

export interface SecureTextInputProps {
  disabled: boolean;
  description?: string;
  onChange: (value: string) => void;
  name: string;
  value: string | null;
}

export default function SecureTextInput(props: SecureTextInputProps): JSX.Element | null {
  const [enteringValue, setEnteringValue] = useState(!props.value);
  const [newValue, setNewValue] = useState('');
  const {classes, cx} = useStyles();
  // Loaded an encrypted previously-stored value from the back end.
  const hasStoredValue = !enteringValue && props.value;

  function clearValue(): void {
    setEnteringValue(true);
    setNewValue('');
    props.onChange('');
  }

  const textInput = (
    <PasswordInput
      aria-label={props.name}
      classNames={{
        ...classes,
        visibilityToggle: cx(classes.visibilityToggle, 'button--circleIcon'),
      }}
      description={!hasStoredValue && props.description}
      disabled={Boolean(hasStoredValue)}
      inputWrapperOrder={['label', 'input', 'description', 'error']}
      onChange={(event) => {
        if (enteringValue) {
          const inputValue = event.target.value;
          setNewValue(inputValue);
          props.onChange(inputValue);
        }
      }}
      // disable Copy secrets to clipboard from a secure_text field
      onCopy={(e: React.ClipboardEvent) => {
        e.preventDefault();
        return false;
      }}
      size="md"
      value={enteringValue ? newValue : '********'}
      visibilityToggleIcon={makeVisibilityToggleIconComponent(props.name)}
    />
  );
  return hasStoredValue ? (
    <Flex>
      <div className={classes.immutableValueWrapper}>
        <Tooltip
          text="This encrypted value cannot be viewed or edited."
          placement={FloatingPlacement.TOP}
          disableContentSelection={true}
        >
          <div>{textInput}</div>
        </Tooltip>
      </div>
      <Tooltip text="Clear" placement={FloatingPlacement.TOP} disableContentSelection={true}>
        {/* Show button with "X" icon to clear the stored value */}
        <Button
          buttonStyle={ButtonStyle.CIRCLE_ICON}
          size={ButtonSize.SMALL}
          onClick={clearValue}
          icon="cross-thick"
          iconAltText="Clear value"
        />
      </Tooltip>
    </Flex>
  ) : (
    textInput
  );
}
